import axios, { AxiosError } from "axios";
import { Table } from "react-bootstrap";
import { Link } from "react-router-dom";

import { formatLedgerAmount } from "../../helpers/formatLedgerAmount";
import { formatDateByCountry } from "../../helpers/utils";
import ErrorPage from "../ErrorPage";
import PageLoader from "../PageLoader";
import {
  LedgerBalanceItem,
  LedgerBalanceResult,
  LedgerBalanceSortDirection,
  LedgerBalanceSortField,
} from "../../types/ledgerBalances";
import styles from "./index.module.scss";

type Props = {
  data?: LedgerBalanceResult;
  error?: Error | null;
  isLoading: boolean;
  idleMessage?: string;
  country?: string;
  sortBy: LedgerBalanceSortField;
  sortDir: LedgerBalanceSortDirection;
  onSort: (field: LedgerBalanceSortField) => void;
};

const COLUMN_COUNT = 6;

const holderName = (item: LedgerBalanceItem) =>
  [item.firstName, item.lastName].filter(Boolean).join(" ") || "—";

const SortHeader = ({
  label,
  field,
  sortBy,
  sortDir,
  onSort,
}: {
  label: string;
  field: LedgerBalanceSortField;
  sortBy: LedgerBalanceSortField;
  sortDir: LedgerBalanceSortDirection;
  onSort: (field: LedgerBalanceSortField) => void;
}) => {
  const active = sortBy === field;
  return (
    <th
      aria-sort={
        active ? (sortDir === "asc" ? "ascending" : "descending") : "none"
      }
    >
      <button
        type="button"
        className={styles.sortButton}
        data-testid={`ledger-balances-sort-${field}`}
        onClick={() => onSort(field)}
      >
        {label}
        <span className="material-icons" style={{ fontSize: 16 }}>
          {active
            ? sortDir === "asc"
              ? "arrow_upward"
              : "arrow_downward"
            : "unfold_more"}
        </span>
      </button>
    </th>
  );
};

const TableHead = ({
  sortBy,
  sortDir,
  onSort,
}: Pick<Props, "sortBy" | "sortDir" | "onSort">) => (
  <thead>
    <tr>
      <th>Account holder</th>
      <SortHeader
        label="Opened"
        field="clientCreatedAt"
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={onSort}
      />
      <th>Currency</th>
      <SortHeader
        label="Total"
        field="totalBalance"
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={onSort}
      />
      <SortHeader
        label="Available"
        field="availableBalance"
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={onSort}
      />
      <th>On hold</th>
    </tr>
  </thead>
);

const TableRow = ({
  item,
  country,
}: {
  item: LedgerBalanceItem;
  country?: string;
}) => (
  <tr data-testid="ledger-balances-row">
    <td>
      <Link
        className={styles.holderLink}
        to={`/ledger-activity?userId=${encodeURIComponent(item.clientId)}`}
        data-testid="ledger-balances-holder"
      >
        {holderName(item)}
      </Link>
    </td>
    <td>
      {formatDateByCountry(item.clientCreatedAt, "dd MMM yyyy HH:mm", country)}
    </td>
    <td>{item.currency}</td>
    <td data-testid="ledger-balances-total">
      {formatLedgerAmount(item.totalBalance, item.currency)}
    </td>
    <td data-testid="ledger-balances-available">
      {formatLedgerAmount(item.availableBalance, item.currency)}
    </td>
    <td>{formatLedgerAmount(item.holdAmount, item.currency)}</td>
  </tr>
);

const LedgerBalancesTable = ({
  data,
  error,
  isLoading,
  idleMessage,
  country,
  sortBy,
  sortDir,
  onSort,
}: Props) => {
  const head = <TableHead sortBy={sortBy} sortDir={sortDir} onSort={onSort} />;

  if (isLoading) {
    return (
      <PageLoader isLoading={isLoading} content="Loading ledger balances..." />
    );
  }

  if (idleMessage && !data && !error) {
    return (
      <div className={styles.table} data-testid="ledger-balances-idle">
        <Table hover bordered responsive="md">
          {head}
          <tbody>
            <tr>
              <td colSpan={COLUMN_COUNT} className={styles.emptyTable}>
                {idleMessage}
              </td>
            </tr>
          </tbody>
        </Table>
      </div>
    );
  }

  if (error || !data) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{
        errorCode: string;
        errorMessage: string;
      }>;
      return (
        <ErrorPage
          title={axiosError.response?.data?.errorCode}
          message={axiosError.response?.data?.errorMessage}
        />
      );
    }
    return <ErrorPage />;
  }

  return (
    <div className={styles.table} data-testid="ledger-balances-table">
      <Table hover bordered responsive="md">
        {head}
        <tbody>
          {data.items.length > 0 ? (
            data.items.map((item) => (
              <TableRow key={item.accountId} item={item} country={country} />
            ))
          ) : (
            <tr>
              <td colSpan={COLUMN_COUNT} className={styles.emptyTable}>
                No balances match these filters.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default LedgerBalancesTable;
